import React, { useState, useMemo } from 'react';
import _get from 'lodash/get';
import _keyBy from 'lodash/keyBy';
import useAnalytics from 'helpers/useAnalytics';
import { useHistory } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { useStatus } from 'resift';
import deleteWidget from 'fetches/homepageWidgets/deleteWidget';
import createWidget from 'fetches/homepageWidgets/createWidget';
import getWidgets from 'fetches/homepageWidgets/getWidgets';
import showSnackbar from 'helpers/showSnackbar';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { DataGrid } from '@material-ui/data-grid';
import IconButton from '@material-ui/core/IconButton';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import IeSucksBanner from 'molecules/IeSucksBanner';
import PageTitle from 'components/Page/PageTitle';
import DeleteConfirmationDialog from 'molecules/DeleteConfirmationDialog';
import NameDuplicateWidgetDialog from 'molecules/SimpleConfirmationDialog';
import CustomGridPanel from './components/CustomGridPanel';
import CustomToolbar from './components/CustomToolbar';
import { HOME_URL, WIDGETS_URL } from 'components/Router/consts';
import { Widget, WidgetCreateRequest, WidgetType } from 'pages/Homepage/typings';
import { MAX_CHAR_COUNT_NAME } from 'pages/Homepage/helpers/consts';

function WidgetsTab() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const analytics = useAnalytics();
  const widgetTypesArray: Array<WidgetType> = useSelector(state =>
    _get(state, ['homepageWidgets', 'widgetTypes']),
  );
  const widgets: Array<Widget> = useSelector(state => _get(state, ['homepageWidgets', 'widgets']));
  const widgetNames = !widgets.length ? [] : widgets.map(w => w.name);
  const deleteWidgetStatus = useStatus(deleteWidget);
  const createWidgetStatus = useStatus(createWidget);

  showSnackbar({
    status: deleteWidgetStatus,
    message: 'Successfully deleted widget.',
    variant: 'success',
    errorMessage: 'An error occurred while deleting the widget.',
  });

  showSnackbar({
    status: createWidgetStatus,
    message: 'Successfully created widget.',
    variant: 'success',
    errorMessage: 'An error occurred while creating the widget.',
  });

  const rows = useMemo(() => {
    const widgetTypes = _keyBy(widgetTypesArray, 'id');

    return widgets.map(w => ({
      ...w,
      type: widgetTypes[w.widgetTypeId].name,
      isOnLayout: w._isInDefaultLayout,
    }));
  }, [widgetTypesArray, widgets]);

  const [anchorElForMoreMenu, setAnchorElForMoreMenu] = useState<null | HTMLElement>(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState<boolean>(false);
  const [selectedWidget, setSelectedWidget] = useState<Widget | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogInput, setDeleteDialogInput] = useState('');
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const selectedWidgetName = _get(selectedWidget, 'name', 'this widget');
  const selectedWidgetId = _get(selectedWidget, 'id') as string;
  const [duplicateDialogInput, setDuplicateDialogInput] = useState(selectedWidgetName);
  const [customSorted, setCustomSorted] = useState(false);

  const nameValidationError = useMemo(() => {
    let error;

    if (!duplicateDialogInput.length) {
      error = 'Name is required';
    }

    if (widgetNames.includes(duplicateDialogInput)) {
      error = 'This name already exists, please use a different name';
    }

    if (duplicateDialogInput.length > MAX_CHAR_COUNT_NAME) {
      error = `Name cannot be more than ${MAX_CHAR_COUNT_NAME} characters`;
    }

    return error;
  }, [duplicateDialogInput]);

  const handleMore = (event: React.MouseEvent<HTMLElement>, widget: Widget | null) => {
    event.stopPropagation();
    setMoreMenuOpen(!moreMenuOpen);
    setSelectedWidget(widget);
    setAnchorElForMoreMenu(event ? event.currentTarget : anchorElForMoreMenu);
  };

  const handleDeleteDialogOpen = () => {
    analytics.track('Delete clicked', {
      category: 'AD - Homepage',
    });

    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setMoreMenuOpen(false);
    setDeleteDialogInput('');
  };

  const handleDeleteDialogInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDeleteDialogInput(e.target.value);
  };

  const handleConfirmDelete = async () => {
    handleDeleteDialogClose();
    await dispatch(deleteWidget(selectedWidgetId));
    dispatch(getWidgets(true));
  };

  const handleDuplicateDialogOpen = () => {
    analytics.track('Duplicate clicked', {
      category: 'AD - Homepage',
    });

    setDuplicateDialogOpen(true);
    setDuplicateDialogInput(selectedWidgetName);
  };

  const handleDuplicateDialogClose = () => {
    setDuplicateDialogOpen(false);
    setMoreMenuOpen(false);
    setDuplicateDialogInput('');
  };

  const handleSaveDuplicateWidget = async () => {
    const widgetTypeId = _get(selectedWidget, 'widgetTypeId') as string;
    const description = _get(selectedWidget, 'description');
    const settings = _get(selectedWidget, 'settings', {});
    handleDuplicateDialogClose();

    const widgetCreateRequest = {
      name: duplicateDialogInput,
      settings,
      widgetTypeId,
    } as WidgetCreateRequest;

    if (description) {
      widgetCreateRequest.description = description;
    }

    await dispatch(createWidget(widgetCreateRequest));
    dispatch(getWidgets(true));
  };

  const handleDuplicateDialogInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDuplicateDialogInput(e.target.value);
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      type: 'number',
      hide: true,
      filterable: false,
    },
    {
      field: 'name',
      headerName: 'Name',
      type: 'string',
      flex: 3,
    },
    {
      field: 'type',
      headerName: 'Type',
      type: 'string',
      flex: 2,
      filterable: false,
    },
    {
      field: 'origination',
      headerName: 'Origination',
      type: 'string',
      flex: 2,
      filterable: false,
      renderCell: (params: any) => {
        const { isUserCreated } = params.row;
        return (
          <>
            <FiberManualRecordIcon
              className={classes.fiberManualRecordIcon}
              color={isUserCreated ? 'secondary' : undefined}
            />
            {isUserCreated ? 'Custom' : 'Prebuilt'}
          </>
        );
      },
      valueGetter: (params: any) => {
        return params.row.isUserCreated ? 'Custom' : 'Prebuilt';
      },
    },
    {
      field: 'on-layout',
      headerName: 'On Layout',
      type: 'string',
      flex: 2,
      filterable: false,
      renderCell: (params: any) => {
        if (!params.row.isOnLayout) {
          return <NotInterestedIcon className={classes.notInterestedIcon} />;
        } else {
          return <CheckCircleIcon color="secondary" />;
        }
      },
      valueGetter: (params: any) => {
        return params.row.isOnLayout ? 'On Layout' : 'Not on Layout';
      },
    },
    {
      field: 'more-actions',
      renderHeader: () => {
        return <span className={classes.onlyShownForSr}>More Actions</span>;
      },
      columHeaderHeight: 0,
      flex: 1,
      disableClickEventBubbling: true,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <div className={classes.moreContainer}>
            <IconButton
              aria-label="More"
              aria-haspopup="true"
              onClick={(event: React.MouseEvent<HTMLElement>) => handleMore(event, params.row)}
            >
              <MoreHorizIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElForMoreMenu}
              open={moreMenuOpen}
              onClose={() => setMoreMenuOpen(false)}
              elevation={1}
            >
              <MenuItem onClick={handleDuplicateDialogOpen}>Duplicate</MenuItem>
              {_get(selectedWidget, 'isUserCreated', false) && (
                <MenuItem onClick={handleDeleteDialogOpen}>Delete</MenuItem>
              )}
            </Menu>
          </div>
        );
      },
    },
  ];

  const handleRowClick = (params: any) => {
    const rowId = params.row.id;
    history.push(`${HOME_URL}${WIDGETS_URL}/${rowId}`);
  };

  const onSortModelChange = (sortModel: any) => {
    !customSorted && setCustomSorted(true);
    const sortColumn = _get(sortModel.sortModel[0], 'field', undefined);

    sortColumn &&
      customSorted &&
      analytics.track('Sort Button Clicked', {
        category: 'AD - Homepage',
        label: sortColumn,
      });
  };

  return (
    <>
      <PageTitle subtitle="Manage available widgets to be added to your homepage. Click the “Create Widget” button to see a list of available widget types." />
      <div className={classes.wrapper}>
        <IeSucksBanner />

        <DataGrid
          className={classes.row}
          rows={rows}
          columns={columns}
          onSortModelChange={sortModel => onSortModelChange(sortModel)}
          disableColumnMenu
          disableColumnSelector
          disableColumnReorder
          disableSelectionOnClick
          onRowClick={handleRowClick}
          sortModel={[{ field: 'name', sort: 'asc' }]}
          components={{
            Toolbar: CustomToolbar,
            // @ts-ignore
            Panel: CustomGridPanel,
          }}
          componentsProps={{
            toolbar: {
              buttonText: 'Create A Widget',
            },
          }}
        />
      </div>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        dialogTitle={`Are you sure you want to delete "${selectedWidgetName}"?`}
        saveButtonText="Delete"
        onClose={handleDeleteDialogClose}
        onSave={handleConfirmDelete}
        disableSave={deleteDialogInput !== selectedWidgetName}
      >
        <Typography variant="subtitle2">
          To delete this widget, type{' '}
          <strong className={classes.dialogWidgetName}>"{selectedWidgetName}" </strong>
          in the input below.
          <br />
          <br />
          WARNING: <strong>You cannot undo this action!</strong>
        </Typography>
        <TextField
          label={`Type ${selectedWidgetName}`}
          className={classes.textField}
          value={deleteDialogInput}
          inputProps={{ 'aria-label': 'widget name input' }}
          onChange={handleDeleteDialogInput}
          autoFocus
        />
      </DeleteConfirmationDialog>
      <NameDuplicateWidgetDialog
        title="Name your Duplicate Widget"
        open={duplicateDialogOpen}
        onCancel={handleDuplicateDialogClose}
        onSubmit={handleSaveDuplicateWidget}
        disableSubmitButton={Boolean(nameValidationError)}
      >
        <Typography className={classes.duplicateInstruction}>
          A new widget will be created with the same configuration. Give it a new name below.
        </Typography>
        <TextField
          className={classes.textField}
          value={duplicateDialogInput}
          inputProps={{ 'aria-label': 'widget name input' }}
          onChange={handleDuplicateDialogInput}
          autoFocus
        />
        {Boolean(nameValidationError) && (
          <FormHelperText error>{nameValidationError}</FormHelperText>
        )}
      </NameDuplicateWidgetDialog>
    </>
  );
}

const useStyles = makeStyles(theme => ({
  root: {},
  wrapper: {
    height: '100vh',
    width: '100%',
  },
  row: {
    cursor: 'pointer',
    border: 'none',
  },
  fiberManualRecordIcon: {
    width: 8,
    height: 8,
    marginRight: theme.spacing(0.75),
  },
  moreContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'end',
  },
  notInterestedIcon: {
    // @ts-ignore
    color: theme.colors.gray1,
  },
  dialogWidgetName: {
    color: theme.palette.error.main,
  },
  textField: {
    width: 400,
  },
  duplicateInstruction: {
    marginBottom: theme.spacing(2),
  },
  onlyShownForSr: {
    overflow: 'hidden',
    height: 0,
    width: 0,
  },
}));

export default WidgetsTab;
